<template>
  <div class="as">
    <!-- ───────────── Hero: title, lock state, global actions ───────────── -->
    <section
      class="as-hero"
      :class="lockStatus.is_locked ? 'is-locked' : 'is-open'"
    >
      <div class="as-hero__main">
        <span class="as-hero__icon">
          <feather-icon
            :icon="lockStatus.is_locked ? 'LockIcon' : 'ShieldIcon'"
            size="26"
          />
        </span>
        <div>
          <h4 class="as-hero__title">
            Active Sessions
          </h4>
          <p class="as-hero__sub">
            See who is signed in and control access to the system.
          </p>
          <span
            class="as-pill"
            :class="lockStatus.is_locked ? 'as-pill--danger' : 'as-pill--ok'"
          >
            <span class="as-pill__dot" />
            {{ lockStatus.is_locked ? 'Logins locked' : 'Logins open' }}
          </span>
        </div>
      </div>

      <div class="as-hero__actions">
        <button
          v-if="!lockStatus.is_locked"
          type="button"
          class="as-btn as-btn--warn"
          :disabled="lockLoading"
          @click="promptLock"
        >
          <feather-icon
            icon="LockIcon"
            size="16"
          />
          Lock new logins
        </button>
        <button
          v-else
          type="button"
          class="as-btn as-btn--ok"
          :disabled="lockLoading"
          @click="unlock"
        >
          <feather-icon
            icon="UnlockIcon"
            size="16"
          />
          Unlock logins
        </button>

        <button
          type="button"
          class="as-btn as-btn--danger"
          :disabled="revokeAllLoading"
          @click="promptRevokeAll"
        >
          <feather-icon
            icon="PowerIcon"
            size="16"
          />
          Force logout everyone
        </button>
      </div>
    </section>

    <div
      v-if="lockStatus.is_locked"
      class="as-lock-note"
    >
      <feather-icon
        icon="AlertTriangleIcon"
        size="18"
      />
      <span>
        Locked by <strong>{{ lockStatus.admin && lockStatus.admin.name }}</strong>
        <template v-if="lockStatus.reason"> — {{ lockStatus.reason }}</template>
        · admins can still log in.
      </span>
    </div>

    <!-- ───────────── Quick stats ───────────── -->
    <div class="as-stats">
      <div class="as-stat as-stat--blue">
        <span class="as-stat__icon">
          <feather-icon
            icon="UsersIcon"
            size="22"
          />
        </span>
        <div>
          <span class="as-stat__label">Sessions found</span>
          <span class="as-stat__value">{{ totalRows }}</span>
        </div>
      </div>

      <div class="as-stat as-stat--amber">
        <span class="as-stat__icon">
          <feather-icon
            icon="CheckSquareIcon"
            size="22"
          />
        </span>
        <div>
          <span class="as-stat__label">Selected</span>
          <span class="as-stat__value">{{ selectedIds.length }}</span>
        </div>
      </div>

      <div
        class="as-stat"
        :class="lockStatus.is_locked ? 'as-stat--red' : 'as-stat--green'"
      >
        <span class="as-stat__icon">
          <feather-icon
            :icon="lockStatus.is_locked ? 'LockIcon' : 'UnlockIcon'"
            size="22"
          />
        </span>
        <div>
          <span class="as-stat__label">Login access</span>
          <span class="as-stat__value">{{ lockStatus.is_locked ? 'Locked' : 'Open' }}</span>
        </div>
      </div>
    </div>

    <!-- ───────────── Bulk actions ───────────── -->
    <div
      v-if="selectedIds.length"
      class="as-bulk"
    >
      <span class="as-bulk__count">
        <span class="as-bulk__badge">{{ selectedIds.length }}</span>
        session{{ selectedIds.length === 1 ? '' : 's' }} selected
      </span>
      <div class="as-bulk__actions">
        <button
          type="button"
          class="as-btn as-btn--warn"
          :disabled="bulkLoading"
          @click="promptRevokeSelected"
        >
          <feather-icon
            icon="LogOutIcon"
            size="16"
          />
          Revoke selected
        </button>
        <button
          type="button"
          class="as-btn as-btn--danger-soft"
          :disabled="bulkLoading"
          @click="promptDeleteSelected"
        >
          <feather-icon
            icon="Trash2Icon"
            size="16"
          />
          Delete selected
        </button>
      </div>
    </div>

    <!-- ───────────── Filters + table ───────────── -->
    <section class="as-panel">
      <div class="as-filters">
        <label class="as-field">
          <feather-icon
            icon="SearchIcon"
            size="18"
          />
          <input
            v-model="filters.search"
            type="text"
            placeholder="Search name or email"
            @keyup.enter="applyFilters"
          >
          <button
            v-if="filters.search"
            type="button"
            class="as-field__clear"
            title="Clear search"
            @click="clearSearch"
          >
            <feather-icon
              icon="XCircleIcon"
              size="16"
            />
          </button>
        </label>

        <session-select
          v-model="filters.role"
          :options="roleOptions"
          icon="BriefcaseIcon"
          @change="applyFilters"
        />

        <session-select
          v-model="filters.country"
          :options="countryOptions"
          icon="GlobeIcon"
          @change="applyFilters"
        />

        <session-select
          v-model="filters.status"
          :options="statusOptions"
          icon="ActivityIcon"
          @change="applyFilters"
        />

        <session-select
          v-model="perPage"
          :options="[10, 20, 50, 100, 200, 500]"
          icon="ListIcon"
          title="Rows per page"
          @change="onPerPageChange"
        />
      </div>

      <div
        v-if="loading"
        class="as-loading"
      >
        <b-spinner
          type="grow"
          label="Loading..."
        />
        <span>Loading sessions…</span>
      </div>

      <template v-else>
        <div
          v-if="sessions.length"
          class="as-toolbar"
        >
          <b-form-checkbox
            :checked="allOnPageSelected"
            @change="toggleSelectAllOnPage"
          >
            Select all on this page
          </b-form-checkbox>
          <span class="as-toolbar__hint">
            Showing {{ sessions.length }} of {{ totalRows }}
          </span>
        </div>

        <div
          v-if="sessions.length"
          class="as-grid"
        >
          <article
            v-for="session in sessions"
            :key="session.id"
            class="as-card"
            :class="{
              'is-selected': selectedIds.includes(session.id),
              'is-revoked': session.status === 'revoked',
            }"
          >
            <header class="as-card__top">
              <b-form-checkbox
                :checked="selectedIds.includes(session.id)"
                @change="toggleSelect(session.id)"
              />
              <span
                class="as-pill"
                :class="session.status === 'revoked' ? 'as-pill--muted' : 'as-pill--ok'"
              >
                <span class="as-pill__dot" />
                {{ session.status === 'revoked' ? 'Revoked' : 'Active' }}
              </span>
            </header>

            <div class="as-card__identity">
              <span
                class="as-user__avatar as-user__avatar--lg"
                :class="`as-user__avatar--${avatarTone(session)}`"
              >
                {{ initials(session.name || session.email) }}
              </span>
              <h6 class="as-card__name">
                {{ session.name }}
              </h6>
              <span class="as-card__email">{{ session.email }}</span>
              <div class="as-card__roles">
                <span
                  v-for="role in session.roles"
                  :key="role"
                  class="as-role"
                  :class="{ 'as-role--admin': isAdminRole(role) }"
                >
                  {{ role }}
                </span>
              </div>
            </div>

            <ul class="as-card__info">
              <li>
                <span class="as-meta__icon">
                  <feather-icon
                    :icon="deviceIcon(session.device)"
                    size="16"
                  />
                </span>
                <div>
                  <small class="as-meta__sub">Device</small>
                  <div class="as-meta__main">
                    {{ session.device || '—' }}
                  </div>
                </div>
              </li>
              <li>
                <span class="as-meta__icon">
                  <feather-icon
                    icon="MapPinIcon"
                    size="16"
                  />
                </span>
                <div>
                  <small class="as-meta__sub">
                    {{ [session.city, session.country].filter(Boolean).join(', ') || 'Unknown location' }}
                  </small>
                  <div class="as-meta__main">
                    {{ session.ip_address || '—' }}
                  </div>
                </div>
              </li>
              <li :title="session.last_used_at ? new Date(session.last_used_at).toLocaleString() : ''">
                <span class="as-meta__icon">
                  <feather-icon
                    icon="ClockIcon"
                    size="16"
                  />
                </span>
                <div>
                  <small class="as-meta__sub">Last active</small>
                  <div class="as-meta__main">
                    {{ session.last_used_at ? timeAgo(session.last_used_at) : '—' }}
                  </div>
                </div>
              </li>
            </ul>

            <footer class="as-card__actions">
              <button
                v-if="session.status !== 'revoked'"
                type="button"
                class="as-btn as-btn--warn as-btn--sm"
                @click="promptRevoke(session)"
              >
                <feather-icon
                  icon="LogOutIcon"
                  size="15"
                />
                Revoke
              </button>
              <button
                type="button"
                class="as-btn as-btn--danger-soft as-btn--sm"
                @click="promptDelete(session)"
              >
                <feather-icon
                  icon="Trash2Icon"
                  size="15"
                />
                Delete
              </button>
            </footer>
          </article>
        </div>

        <div
          v-else
          class="as-empty"
        >
          <span class="as-empty__icon">
            <feather-icon
              icon="MonitorIcon"
              size="28"
            />
          </span>
          <h6 class="as-empty__title">
            No sessions found
          </h6>
          <span>Try changing the filters or the search term.</span>
        </div>
      </template>

      <Pagination
        :page="currentPage"
        :per-page="perPage"
        :total-rows="totalRows"
        @get-all-data="onPageChange"
      />
    </section>
  </div>
</template>

<script>
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import Pagination from '@/components/general-table/specific-utils/Pagination.vue'
import SessionSelect from './SessionSelect.vue'
import decryptData from '@/store/auth/decryptData'

export default {
  components: { Pagination, SessionSelect },
  data() {
    return {
      apiUrl: 'sessions',
      lockStatus: { is_locked: false, admin: null, reason: null },
      lockLoading: false,
      revokeAllLoading: false,
      loading: false,
      sessions: [],
      totalRows: 0,
      currentPage: 1,
      perPage: 20,
      filters: {
        search: '', role: '', country: '', status: 'active',
      },
      roles: [],
      countries: [],
      statusOptions: [
        { value: 'active', text: 'Active' },
        { value: 'revoked', text: 'Revoked' },
        { value: 'all', text: 'All statuses' },
      ],
      selectedIds: [],
      bulkLoading: false,
    }
  },
  computed: {
    roleOptions() {
      return [{ value: '', text: 'All roles' }, ...this.roles.map(r => ({ value: r, text: r }))]
    },
    countryOptions() {
      return [{ value: '', text: 'All countries' }, ...this.countries.map(c => ({ value: c, text: c }))]
    },
    allOnPageSelected() {
      return this.sessions.length > 0 && this.sessions.every(s => this.selectedIds.includes(s.id))
    },
  },
  mounted() {
    this.fetchLockStatus()
    this.fetchSessions()
  },
  methods: {
    notify(title, variant = 'success') {
      this.$toast({
        component: ToastificationContent,
        props: { title, icon: 'BellIcon', variant },
      })
    },
    errorMessage(error) {
      try {
        return decryptData(error.response.data.payload).message
      } catch (e) {
        return 'An unknown error occurred'
      }
    },

    /* ---------- display helpers ---------- */
    initials(text) {
      const parts = String(text || '?').trim().split(/\s+/)
      return (parts.length > 1 ? parts[0][0] + parts[1][0] : parts[0].slice(0, 2)).toUpperCase()
    },
    avatarTone(item) {
      const key = String(item.id || item.email || '')
      let hash = 0
      for (let i = 0; i < key.length; i += 1) hash = (hash + key.charCodeAt(i)) % 4
      return hash
    },
    isAdminRole(role) {
      return /admin/i.test(role)
    },
    deviceIcon(device) {
      const text = String(device || '').toLowerCase()
      if (/ipad|tablet/.test(text)) return 'TabletIcon'
      if (/iphone|android|mobile|phone/.test(text)) return 'SmartphoneIcon'
      return 'MonitorIcon'
    },
    timeAgo(value) {
      const seconds = Math.max(0, Math.floor((Date.now() - new Date(value).getTime()) / 1000))
      if (seconds < 60) return 'Just now'
      const units = [
        [86400, 'day'],
        [3600, 'hour'],
        [60, 'minute'],
      ]
      const [size, label] = units.find(([s]) => seconds >= s)
      const count = Math.floor(seconds / size)
      return `${count} ${label}${count === 1 ? '' : 's'} ago`
    },

    /* ---------- list ---------- */
    applyFilters() {
      this.currentPage = 1
      this.fetchSessions()
    },
    clearSearch() {
      this.filters.search = ''
      this.applyFilters()
    },
    onPerPageChange() {
      this.currentPage = 1
      this.fetchSessions()
    },
    onPageChange(page) {
      this.currentPage = page
      this.fetchSessions()
    },
    async fetchSessions() {
      this.loading = true
      try {
        const response = await axios.get(this.apiUrl, {
          params: {
            search: this.filters.search || undefined,
            role: this.filters.role || undefined,
            country: this.filters.country || undefined,
            status: this.filters.status,
            skip: (this.currentPage - 1) * this.perPage,
            limit: this.perPage,
          },
        })
        const { data } = decryptData(response.data.payload)
        this.sessions = data.sessions
        this.totalRows = data.count
        this.roles = data.roles
        this.countries = data.countries
        this.selectedIds = []
      } catch (error) {
        this.notify(this.errorMessage(error), 'danger')
      } finally {
        this.loading = false
      }
    },
    toggleSelect(id) {
      this.selectedIds = this.selectedIds.includes(id)
        ? this.selectedIds.filter(i => i !== id)
        : [...this.selectedIds, id]
    },
    toggleSelectAllOnPage() {
      this.selectedIds = this.allOnPageSelected ? [] : this.sessions.map(s => s.id)
    },

    /* ---------- login lock ---------- */
    async fetchLockStatus() {
      try {
        const response = await axios.get('sessions/lock-status')
        this.lockStatus = decryptData(response.data.payload).data
      } catch (error) {
        this.notify(this.errorMessage(error), 'danger')
      }
    },
    promptLock() {
      this.$swal({
        title: 'Lock new logins?',
        text: 'Staff will not be able to log in until you unlock this. Admins can still log in.',
        input: 'text',
        inputPlaceholder: 'Reason (optional)',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#f0a73a',
        confirmButtonText: 'Lock logins',
      }).then(result => {
        if (result.isConfirmed) this.lock(result.value)
      })
    },
    async lock(reason) {
      this.lockLoading = true
      try {
        await axios.post('sessions/lock', { reason: reason || null })
        this.notify('New logins are now locked')
        this.fetchLockStatus()
      } catch (error) {
        this.notify(this.errorMessage(error), 'danger')
      } finally {
        this.lockLoading = false
      }
    },
    async unlock() {
      this.lockLoading = true
      try {
        await axios.post('sessions/unlock')
        this.notify('Logins are unlocked')
        this.fetchLockStatus()
      } catch (error) {
        this.notify(this.errorMessage(error), 'danger')
      } finally {
        this.lockLoading = false
      }
    },

    /* ---------- single session ---------- */
    promptRevoke(session) {
      this.$swal({
        title: 'Revoke this session?',
        text: `${session.name || session.email} will be signed out immediately.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#e5484d',
        confirmButtonText: 'Revoke',
      }).then(result => {
        if (result.isConfirmed) this.revoke(session.id)
      })
    },
    async revoke(id) {
      try {
        await axios.post(`sessions/${id}/revoke`)
        this.notify('Session revoked')
        this.fetchSessions()
      } catch (error) {
        this.notify(this.errorMessage(error), 'danger')
      }
    },
    promptDelete(session) {
      this.$swal({
        title: 'Delete this session permanently?',
        text: `This removes ${session.name || session.email}'s session row entirely — it will no longer show up as revoked history either.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#e5484d',
        confirmButtonText: 'Delete',
      }).then(result => {
        if (result.isConfirmed) this.deleteOne(session.id)
      })
    },
    async deleteOne(id) {
      try {
        await axios.delete(`sessions/${id}`)
        this.notify('Session deleted')
        this.fetchSessions()
      } catch (error) {
        this.notify(this.errorMessage(error), 'danger')
      }
    },

    /* ---------- bulk ---------- */
    promptRevokeSelected() {
      this.$swal({
        title: `Revoke ${this.selectedIds.length} selected session(s)?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#e5484d',
        confirmButtonText: 'Revoke selected',
      }).then(result => {
        if (result.isConfirmed) this.revokeSelected()
      })
    },
    async revokeSelected() {
      this.bulkLoading = true
      try {
        const response = await axios.post('sessions/revoke-selected', { ids: this.selectedIds })
        const { revoked } = decryptData(response.data.payload).data
        this.notify(`Revoked ${revoked} session(s)`)
        this.fetchSessions()
      } catch (error) {
        this.notify(this.errorMessage(error), 'danger')
      } finally {
        this.bulkLoading = false
      }
    },
    promptDeleteSelected() {
      this.$swal({
        title: `Permanently delete ${this.selectedIds.length} selected session(s)?`,
        text: 'This removes the rows entirely — cannot be undone.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#e5484d',
        confirmButtonText: 'Delete selected',
      }).then(result => {
        if (result.isConfirmed) this.deleteSelected()
      })
    },
    async deleteSelected() {
      this.bulkLoading = true
      try {
        const response = await axios.post('sessions/delete-selected', { ids: this.selectedIds })
        const { deleted } = decryptData(response.data.payload).data
        this.notify(`Deleted ${deleted} session(s)`)
        this.fetchSessions()
      } catch (error) {
        this.notify(this.errorMessage(error), 'danger')
      } finally {
        this.bulkLoading = false
      }
    },

    /* ---------- force logout ---------- */
    promptRevokeAll() {
      this.$swal({
        title: 'Force logout everyone?',
        text: 'This immediately signs out every user and admin currently logged in, everywhere. Type "logout" to confirm.',
        icon: 'warning',
        input: 'text',
        inputPlaceholder: 'Type logout to confirm',
        showCancelButton: true,
        confirmButtonColor: '#e5484d',
        confirmButtonText: 'Log everyone out',
        inputValidator: value => (value === 'logout' ? null : 'Type "logout" (lowercase) to confirm'),
      }).then(result => {
        if (result.isConfirmed) this.revokeAll()
      })
    },
    async revokeAll() {
      this.revokeAllLoading = true
      try {
        const response = await axios.post('sessions/revoke-all')
        const { tokens_revoked: revoked } = decryptData(response.data.payload).data
        this.notify(`Logged everyone out (${revoked} sessions revoked)`)
        this.fetchSessions()
      } catch (error) {
        this.notify(this.errorMessage(error), 'danger')
      } finally {
        this.revokeAllLoading = false
      }
    },
  },
}
</script>

<style lang="scss">
@import '@/assets/scss/pages/active-sessions/active-sessions.scss';
</style>
